import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import VerifiedIcon from "@mui/icons-material/Verified";
import InfoIcon from "@mui/icons-material/Info";
import HistoryIcon from "@mui/icons-material/History";
import { useDispatch, useSelector } from "react-redux";
import Wrapper from "../../../components/Wrapper";
import title from "../../../components/title";
import {
  GetLandlordEscrowHistory,
  ConfirmEscrowRelease,
} from "../../../redux/reducer/EscrowTransaction";
import { formatCurrency, getCommissionRates, updateCommissionRates } from "../../../utils/commissionCalculator";
import { GetPublicSettings } from "../../../redux/reducer/SystemSettings";
import moment from "moment";

const EscrowDashboard = () => {
  title("Escrow Management");
  const dispatch = useDispatch();

  const open = useSelector((state) => state.sidebar.open);
  const { transactions: landlordTransactions, loading } = useSelector(
    (state) => state.escrow
  );
  const { settings: commissionSettings } = useSelector((state) => state.systemSettings);

  const [activeTab, setActiveTab] = useState(0);
  const [releaseDialog, setReleaseDialog] = useState({ open: false, transaction: null });
  const [releaseLoading, setReleaseLoading] = useState(false);

  const landlordId = localStorage.getItem("user_id");

  // Fetch commission settings on mount
  useEffect(() => {
    dispatch(GetPublicSettings());
  }, [dispatch]);

  // Update commission rates when settings change
  useEffect(() => {
    if (commissionSettings) {
      updateCommissionRates(commissionSettings);
    }
  }, [commissionSettings]);

  // Get current commission rates for display
  const currentRates = getCommissionRates();

  // Fetch escrow transactions on mount
  useEffect(() => {
    if (landlordId) {
      dispatch(GetLandlordEscrowHistory({ landlordId }));
    }
  }, [dispatch, landlordId]);

  // Ensure transactions is always an array
  const transactionsArray = Array.isArray(landlordTransactions) ? landlordTransactions : [];

  // Filter transactions based on tab
  const getFilteredTransactions = () => {
    if (activeTab === 1) {
      return transactionsArray.filter((t) => t.status === "held");
    } else if (activeTab === 2) {
      return transactionsArray.filter((t) => t.status === "released");
    }

    return transactionsArray;
  };

  // Confirm and release escrow
  const handleConfirmRelease = async () => {
    if (!releaseDialog.transaction) return;

    setReleaseLoading(true);
    try {
      await dispatch(
        ConfirmEscrowRelease({
          escrowId: releaseDialog.transaction._id,
          landlordId,
        })
      );
      setReleaseDialog({ open: false, transaction: null });
      // Refresh transactions
      dispatch(GetLandlordEscrowHistory({ landlordId }));
    } catch (error) {
      console.error("Failed to release escrow:", error);
    } finally {
      setReleaseLoading(false);
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "warning";
      case "held":
        return "info";
      case "verified":
        return "primary";
      case "released":
        return "success";
      default:
        return "default";
    }
  };

  // Get escrow step
  const getEscrowStep = (status) => {
    switch (status) {
      case "pending":
        return 0;
      case "held":
        return 1;
      case "verified":
        return 2;
      case "released":
        return 3;
      default:
        return 0;
    }
  };

  // Calculate summary
  const getSummary = () => {
    const heldAmount = transactionsArray
      .filter((t) => t.status === "held")
      .reduce((sum, t) => sum + (t.landlordReceives || t.amount || 0), 0);
    const releasedAmount = transactionsArray
      .filter((t) => t.status === "released")
      .reduce((sum, t) => sum + (t.landlordReceives || t.amount || 0), 0);
    const heldCount = transactionsArray.filter((t) => t.status === "held").length;
    const releasedCount = transactionsArray.filter((t) => t.status === "released").length;

    return { heldAmount, releasedAmount, heldCount, releasedCount };
  };

  const summary = getSummary();
  const filteredTransactions = getFilteredTransactions();

  // Render escrow card
  const renderEscrowCard = (transaction) => (
    <Grid item xs={12} md={6} key={transaction._id}>
      <Card
        elevation={2}
        sx={{
          borderRadius: 3,
          border: transaction.status === "held" ? "2px solid" : "1px solid",
          borderColor: transaction.status === "held" ? "info.main" : "#e0e0e0",
        }}
      >
        <CardContent>
          {/* Header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <PersonIcon fontSize="small" color="primary" />
                <Typography variant="subtitle1" fontWeight={600}>
                  {transaction.tenantName || "Tenant"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <HomeIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {transaction.flatName || "Property"}
                </Typography>
              </Box>
            </Box>
            <Chip
              icon={transaction.status === "held" ? <LockIcon /> : <LockOpenIcon />}
              label={transaction.status?.toUpperCase()}
              color={getStatusColor(transaction.status)}
              size="small"
            />
          </Box>

          {/* Escrow Progress */}
          <Stepper activeStep={getEscrowStep(transaction.status)} alternativeLabel sx={{ my: 2 }}>
            <Step>
              <StepLabel>Payment</StepLabel>
            </Step>
            <Step>
              <StepLabel>In Escrow</StepLabel>
            </Step>
            <Step>
              <StepLabel>Verified</StepLabel>
            </Step>
            <Step>
              <StepLabel>Released</StepLabel>
            </Step>
          </Stepper>

          <Divider sx={{ my: 2 }} />

          {/* Amount Info */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Tenant Paid
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {formatCurrency(transaction.totalAmount || transaction.amount || 0)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                You Receive
              </Typography>
              <Typography variant="h6" fontWeight={600} color="success.main">
                {formatCurrency(transaction.landlordReceives || transaction.amount || 0)}
              </Typography>
            </Grid>
          </Grid>

          {/* Commission Info */}
          <Paper elevation={0} sx={{ bgcolor: "#f5f5f5", p: 1.5, borderRadius: 2, mt: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                OTS Service Fee ({currentRates.ADVANCE * 100}%)
              </Typography>
              <Typography variant="caption" fontWeight={600}>
                {formatCurrency(transaction.commissionAmount || 0)}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              This amount is deducted as platform fee
            </Typography>
          </Paper>

          {/* Date Info */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {transaction.status === "released"
                ? `Released on ${moment(transaction.releasedAt).format("DD MMM YYYY")}`
                : `Received on ${moment(transaction.createdAt).format("DD MMM YYYY")}`}
            </Typography>
          </Box>

          {/* Release Button */}
          {transaction.status === "held" && (
            <Button
              variant="contained"
              color="success"
              fullWidth
              startIcon={<VerifiedIcon />}
              onClick={() => setReleaseDialog({ open: true, transaction })}
              sx={{ mt: 2, borderRadius: 2 }}
            >
              Verify & Release Payment
            </Button>
          )}

          {/* Released Status */}
          {transaction.status === "released" && (
            <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mt: 2 }}>
              Payment released to your bank account
            </Alert>
          )}
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <>
      <Wrapper open={open} mylocation="/escrow-management" />

      <div className={`${open ? "sidebar-open" : "sidebar-closed"}`}>
        <div className="mainHeading">
          <h1>Escrow Management</h1>
        </div>

        {/* Info Alert */}
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3, borderRadius: 2 }}>
          Escrow protects both parties. When a tenant pays advance, it's held securely until you verify and release.
          OTS charges {currentRates.ADVANCE * 100}% service fee on advance payments.
        </Alert>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card elevation={2} sx={{ borderRadius: 3, bgcolor: "info.main", color: "white" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <LockIcon />
                  <Typography variant="subtitle2">Held in Escrow</Typography>
                </Box>
                <Typography variant="h4" fontWeight={700}>
                  {formatCurrency(summary.heldAmount)}
                </Typography>
                <Typography variant="caption">
                  {summary.heldCount} payment{summary.heldCount !== 1 ? "s" : ""} pending release
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card elevation={2} sx={{ borderRadius: 3, bgcolor: "success.main", color: "white" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <CheckCircleIcon />
                  <Typography variant="subtitle2">Total Released</Typography>
                </Box>
                <Typography variant="h4" fontWeight={700}>
                  {formatCurrency(summary.releasedAmount)}
                </Typography>
                <Typography variant="caption">
                  {summary.releasedCount} payment{summary.releasedCount !== 1 ? "s" : ""} released
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card elevation={2} sx={{ borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <AccountBalanceWalletIcon color="primary" />
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Transactions
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={700} color="primary">
                  {transactionsArray.length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  All escrow transactions
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AccountBalanceWalletIcon fontSize="small" />
                All ({transactionsArray.length})
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LockIcon fontSize="small" />
                In Escrow
                {summary.heldCount > 0 && (
                  <Chip label={summary.heldCount} size="small" color="info" />
                )}
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <HistoryIcon fontSize="small" />
                Released
              </Box>
            }
          />
        </Tabs>

        {/* Escrow Cards */}
        {loading ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredTransactions.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: "center", borderRadius: 3 }}>
            <LockIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Escrow Transactions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              When tenants pay advance for your properties, they will appear here.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {filteredTransactions.map(renderEscrowCard)}
          </Grid>
        )}

        {/* Release Confirmation Dialog */}
        <Dialog
          open={releaseDialog.open}
          onClose={() => setReleaseDialog({ open: false, transaction: null })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Confirm & Release Payment</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              By releasing this payment, you confirm that you have verified the tenant and are ready to start the tenancy.
            </Alert>

            <Paper elevation={0} sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Payment Details
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body2">Tenant</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {releaseDialog.transaction?.tenantName}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body2">Property</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {releaseDialog.transaction?.flatName}
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body2">Tenant Paid</Typography>
                <Typography variant="body2">
                  {formatCurrency(releaseDialog.transaction?.totalAmount || 0)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body2" color="error">
                  OTS Fee ({currentRates.ADVANCE * 100}%)
                </Typography>
                <Typography variant="body2" color="error">
                  -{formatCurrency(releaseDialog.transaction?.commissionAmount || 0)}
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  You Will Receive
                </Typography>
                <Typography variant="subtitle1" fontWeight={700} color="success.main">
                  {formatCurrency(releaseDialog.transaction?.landlordReceives || 0)}
                </Typography>
              </Box>
            </Paper>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              The amount will be transferred to your registered bank account within 1-2 business days.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setReleaseDialog({ open: false, transaction: null })}
              disabled={releaseLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleConfirmRelease}
              disabled={releaseLoading}
              startIcon={releaseLoading ? <CircularProgress size={20} /> : <VerifiedIcon />}
            >
              Confirm & Release
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
};

export default EscrowDashboard;
